import React, { Fragment } from 'react';
import Button from './Button';
import ClipboardIcon from './ClipboardIcon';
import GroupIcon from './GroupIcon';
import Action from './Action';
import CheckCircleIcon from './CheckCircleIcon';
import ComposeBallotModal from './ComposeBallotModal';
import CreateLeagueModal from './CreateLeagueModal';
import LeagueBar, { LeagueBarCountMode } from './LeagueBar';
import LeagueModal from './LeagueModal';
import { AllUsersModal } from './AllUsersModal';
import BallotsCompletedResource from '../resources/Ballot/Completed';
import {
  getHasStartedBallot,
  getIsBallotComplete,
} from '../resources/Ballot/services';
import avatars from '../config/avatars';
import useCopy from '../util/useCopy';
import ObjectiveStateResource from '../resources/ObjectiveState';
import ChooseAvatarModal from './ChooseAvatarModal';
import {
  setHiddenActivityKeyMutation,
  updateUserPropertiesMutation,
} from '../resources/Auth/mutations';
import { PopWrapper } from './PopWrapper';
import { thisOscarsYear } from '../config/constants';
import { CurrentUserResourceType } from '../resources/Auth/CurrentUser';
import { OneLeagueResourceType } from '../resources/League/One';
import { OneBallotResourceType } from '../resources/Ballot/One';
import { makeClassName, pluralize } from '@siteless/util';
import LetterboxdModal from './LetterboxdModal';
import LetterboxdIcon from './LetterboxdIcon';
import { LetterboxdBar } from './LetterboxdBar';
import { badges } from '../resources/Letterboxd/watchProgress';
import { OneUserLetterboxdData } from '../resources/Letterboxd';
import { WatchBadge } from './WatchBadge';
import { tailwindVariables } from '../styles/tailwindVariables';
import chroma from 'chroma-js';
import { PersistentBanner } from './PersistentBanner';
import { usePresentationMode } from '../util/usePresentationMode';
import { CeremonyDetailCard } from './CeremonyDetailCard';
import withSuspense from './withSuspense';
import { IconAlertTriangle } from './IconAlertTriangle';
import Loader from './Loader';
import BadgeFilmsModal, { BadgeKey } from './BadgeFilmsModal';
import NomineesModal from './NomineesModal';
import LaurelLeftIcon from './LaurelLeftIcon';
import LaurelRightIcon from './LaurelRightIcon';
import { typedEntries } from '../util/typedEntries';
import { EnvelopeIcon } from './EnvelopeIcon';
import { avatarDefaultBackgroundColors } from '../config/avatarColors';

let LetterboxdBadgesSection = withSuspense(
  ({
    letterboxdId,
    userId,
    activityKey,
    onBadgeClick,
  }: {
    letterboxdId: string;
    userId: string;
    activityKey: string;
    onBadgeClick: (badgeKey: BadgeKey) => void;
  }) => {
    let copy = useCopy();
    let letterboxdResult = OneUserLetterboxdData.use(letterboxdId);

    if (!letterboxdResult) return null;

    let letterboxdData =
      letterboxdResult.status === 'ok' ? letterboxdResult.data : null;

    return (
      <div className="w-full max-w-sm rounded-xl bg-purple-900 py-2 px-4">
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm font-medium">{copy.Badges}</div>
          <Action
            className="!p-0 !text-xs"
            onClick={() => {
              setHiddenActivityKeyMutation({
                userId,
                activityKey,
                hidden: true,
              });
            }}
          >
            {copy.Hide}
          </Action>
        </div>
        {letterboxdData ? (
          <div className="flex justify-around py-2">
            {typedEntries(badges).map(([badgeKey, badge]) => {
              let { totalCount, watchedCount } = badge.getProgress({
                oscarFilmsStatus: letterboxdData.oscarFilmsStatus,
              });
              let isComplete = watchedCount === totalCount;
              return (
                <div
                  key={badgeKey}
                  className="cursor-pointer"
                  onClick={() => onBadgeClick(badgeKey)}
                >
                  <div className="mb-3">
                    <WatchBadge
                      animateShine
                      renderArtwork={badge.renderBadgeArtwork}
                      label={badge.label}
                      size={100}
                      notCompletedFillColor={
                        tailwindVariables.theme.colors.purple[700]
                      }
                      notCompletedLineColor={
                        tailwindVariables.theme.colors.purple[400]
                      }
                      totalCount={totalCount}
                      completedCount={watchedCount}
                      completedFillColor={badge.completedFillColor}
                      completedLineColor={badge.completedLineColor}
                    />
                  </div>
                  <div
                    className={makeClassName(
                      'rounded-lg py-0.5 text-center text-xs',
                      !isComplete && 'bg-purple-800 text-purple-300',
                    )}
                    style={
                      isComplete
                        ? {
                            backgroundColor: badge.completedFillColor,
                            color: chroma(badge.completedFillColor)
                              .darken(1.9)
                              .saturate(1)
                              .hex(),
                          }
                        : undefined
                    }
                  >
                    <span className="font-medium">{watchedCount}</span> of{' '}
                    <span className="font-medium"> {totalCount}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg bg-purple-800 py-3 px-4 text-purple-300">
            <IconAlertTriangle className="h-4 w-4 flex-none" />
            <div className="text-sm">
              {copy['Letterboxd is unavailable right now']}
            </div>
          </div>
        )}
      </div>
    );
  },
  {
    fallback: (
      <div className="flex w-full max-w-sm items-center justify-center rounded-xl bg-purple-900 py-6">
        <Loader />
      </div>
    ),
  },
);

let CeremonyBefore = ({
  currentUser,
  leagues,
  ballot,
}: {
  currentUser: CurrentUserResourceType;
  leagues: NonNullable<OneLeagueResourceType>[];
  ballot: OneBallotResourceType;
}) => {
  let [presentationMode] = usePresentationMode();
  let { ballotShape, isBallotReady } = ObjectiveStateResource.use();
  let completedBallots = BallotsCompletedResource.use();

  let copy = useCopy();
  let AvatarArtworkComponent =
    avatars[currentUser.avatarId || avatars.AVATAR_0.id].ArtworkComponent;

  let [showHiddenItems, setShowHiddenItems] = React.useState(false);

  let [showLetterboxdModal, setShowLetterboxdModal] = React.useState(false);
  let [showChooseAvatarModal, setShowChooseAvatarModal] = React.useState(false);
  let [showComposeBallotModal, setShowComposeBallotModal] =
    React.useState(false);
  let [isEnvelopeOpen, setIsEnvelopeOpen] = React.useState(false);

  let [showCreateLeagueModal, setShowCreateLeagueModal] = React.useState(false);
  let [showLeagueModalId, setShowLeagueModalId] = React.useState<
    string | undefined
  >();
  let [showAllUsersModal, setShowAllUsersModal] = React.useState(false);
  let [showNomineesModal, setShowNomineesModal] = React.useState(false);
  let [selectedBadgeKey, setSelectedBadgeKey] = React.useState<BadgeKey | null>(
    null,
  );

  const hasJoinedLeague = Boolean(leagues.length);

  const hasStartedBallot = getHasStartedBallot(ballot);
  const isBallotComplete = getIsBallotComplete({ ballot, ballotShape });

  let hasConnectedLetterboxd = Boolean(currentUser.letterboxdId);

  type StateRenderer = (args: {
    isNextIncompleteActivity: boolean;
    standalone?: boolean;
    key: string;
  }) => React.ReactNode;

  const activities: {
    key: string;
    state: 'disabled' | 'new' | 'started' | 'complete';
    label?: string;
    canHide?: boolean;
    renderState: {
      complete?: StateRenderer;
      started?: StateRenderer;
      new?: StateRenderer;
      disabled?: StateRenderer;
    };
  }[] = [
    {
      key: 'ballot',
      state: !isBallotReady
        ? 'disabled'
        : isBallotComplete
        ? 'complete'
        : hasStartedBallot
        ? 'started'
        : 'new',
      canHide: false,
      label: copy.Ballot,
      renderState: {
        complete: () => (
          <div className="mt-10 mb-2 flex w-full max-w-sm flex-col gap-2">
            <div className="flex w-full flex-col items-center justify-center rounded-xl bg-purple-900 px-6 pt-4 pb-2">
              <PopWrapper className="-mt-10 mb-2">
                <EnvelopeIcon
                  width={74}
                  isOpen={isEnvelopeOpen}
                  closeDelay={200}
                  seal
                  sealInitial={currentUser?.handle?.trim()[0]}
                  color={
                    currentUser.avatarBackgroundHex ||
                    avatarDefaultBackgroundColors[
                      currentUser.avatarId || 'AVATAR_0'
                    ]
                  }
                  onIsOpenChange={() => {
                    setIsEnvelopeOpen(true);
                    setTimeout(() => setShowComposeBallotModal(true), 200);
                  }}
                  paperContent={
                    <div className="flex flex-col gap-1">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="flex items-center gap-1">
                          <div className="h-1.5 w-1.5 flex-none rounded-full bg-purple-400" />
                          <div
                            className="h-1 rounded-full bg-purple-200"
                            style={{ width: `${60 + Math.random() * 30}%` }}
                          />
                        </div>
                      ))}
                    </div>
                  }
                />
              </PopWrapper>
              <div className="text-md">{copy.yourBallotIsReadyToGo}</div>
              <Action
                onClick={() => {
                  setShowComposeBallotModal(true);
                }}
              >
                {copy.tapToViewOrEdit}
              </Action>
            </div>
            <div className="flex w-full items-center justify-center gap-2 rounded-xl bg-purple-900 px-4 py-2">
              <LaurelLeftIcon className="h-6 w-6 text-rose-bright" />
              <Action
                onClick={() => {
                  setShowNomineesModal(true);
                }}
              >
                {copy.seeTheNominees}
              </Action>
              <LaurelRightIcon className="h-6 w-6 text-rose-bright" />
            </div>
          </div>
        ),
        started: () => (
          <div className="my-2 flex w-full max-w-sm flex-col gap-2">
            <div className="flex w-full flex-col items-center justify-center rounded-xl bg-purple-900 py-6 px-6">
              <p>{copy.makeSureYouFinishYourBallotBeforeTheOscarsStart}</p>
              <p>
                <Button
                  Icon={ClipboardIcon}
                  onClick={() => {
                    setShowComposeBallotModal(true);
                  }}
                >
                  {copy.finishYourBallot}
                </Button>
              </p>
            </div>
            <div className="flex w-full items-center justify-center gap-2 rounded-xl bg-purple-900 px-4 py-2">
              <LaurelLeftIcon className="h-6 w-6 text-rose-bright" />
              <Action
                onClick={() => {
                  setShowNomineesModal(true);
                }}
              >
                {copy.seeTheNominees}
              </Action>
              <LaurelRightIcon className="h-6 w-6 text-rose-bright" />
            </div>
          </div>
        ),
        new: () => (
          <div className="my-2 flex w-full max-w-sm flex-col gap-2">
            <div className="flex w-full flex-col items-center justify-center rounded-xl bg-purple-900 px-6 py-6">
              <p>{copy.makeSureYouFinishYourBallotBeforeTheOscarsStart}</p>
              <p>
                <Button
                  Icon={ClipboardIcon}
                  onClick={() => {
                    setShowComposeBallotModal(true);
                  }}
                >
                  {copy.fillOutYourBallot}
                </Button>
              </p>
            </div>
            <div className="flex w-full items-center justify-center gap-2 rounded-xl bg-purple-900 px-4 py-2">
              <LaurelLeftIcon className="h-6 w-6 text-rose-bright" />
              <Action
                onClick={() => {
                  setShowNomineesModal(true);
                }}
              >
                {copy.seeTheNominees}
              </Action>
              <LaurelRightIcon className="h-6 w-6 text-rose-bright" />
            </div>
          </div>
        ),
        disabled: () => (
          <div className="my-2 flex flex-col items-center justify-center gap-4 rounded-xl bg-purple-900 px-8 py-6">
            <ClipboardIcon className="h-12 w-12" />
            <div className="text-md">
              {copy.ballotNotReadyToFillMessage({
                year: thisOscarsYear,
              })}
            </div>
          </div>
        ),
      },
    },
    {
      key: 'league',
      label: copy.League,
      state: hasJoinedLeague ? 'complete' : 'new',
      canHide: true,
      renderState: {
        complete: () => null,
        started: () => null,
        new: ({ isNextIncompleteActivity, key }) =>
          isNextIncompleteActivity ? (
            <div className="my-2 flex max-w-sm flex-col items-center justify-center gap-4">
              <div>{copy.wantToPlayAgainstAGroupOfFriends}</div>
              <div className="flex items-center">
                <Button
                  Icon={GroupIcon}
                  onClick={() => {
                    setShowCreateLeagueModal(true);
                  }}
                >
                  {copy.startALeague}
                </Button>
                <Action
                  onClick={() => {
                    setHiddenActivityKeyMutation({
                      userId: currentUser.id,
                      activityKey: key,
                      hidden: true,
                    });
                  }}
                >
                  {copy.Hide}
                </Action>
              </div>
            </div>
          ) : (
            <p>
              {copy.wantToPlayAgainstAGroupOfFriends}
              <br />
              <div className="flex items-center gap-2">
                <Action
                  Icon={GroupIcon}
                  onClick={() => {
                    setShowCreateLeagueModal(true);
                  }}
                >
                  {copy.startALeague}
                </Action>
                <Action
                  onClick={() => {
                    setHiddenActivityKeyMutation({
                      userId: currentUser.id,
                      activityKey: key,
                      hidden: true,
                    });
                  }}
                >
                  {copy.Hide}
                </Action>
              </div>
            </p>
          ),
        disabled: () => null,
      },
    },
    {
      key: 'letterboxd',
      label: copy['Letterboxd prompt'],
      state: hasConnectedLetterboxd ? 'complete' : 'new',
      canHide: true,
      renderState: {
        complete: () => null,
        started: () => null,
        new: ({ isNextIncompleteActivity, key }) => (
          <div className="my-2 flex max-w-xs flex-col items-center justify-center gap-4 rounded-xl bg-purple-900 px-6 pt-4 pb-4 text-center">
            <div>{copy.connectYourLetterboxd}</div>
            <div className="flex items-center">
              {isNextIncompleteActivity ? (
                <Button
                  Icon={LetterboxdIcon}
                  onClick={() => {
                    setShowLetterboxdModal(true);
                  }}
                >
                  {copy.connect}
                </Button>
              ) : (
                <Action
                  Icon={LetterboxdIcon}
                  onClick={() => {
                    setShowLetterboxdModal(true);
                  }}
                >
                  {copy.connect}
                </Action>
              )}
              <Action
                onClick={() => {
                  setHiddenActivityKeyMutation({
                    userId: currentUser.id,
                    activityKey: key,
                    hidden: true,
                  });
                }}
              >
                {copy.Hide}
              </Action>
            </div>
          </div>
        ),
        disabled: () => null,
      },
    },
    {
      key: 'watchProgress',
      label: copy.Badges,
      state: hasConnectedLetterboxd ? 'complete' : 'disabled',
      canHide: true,
      renderState: {
        complete: ({ key }) =>
          !currentUser.letterboxdId ? null : (
            <LetterboxdBadgesSection
              letterboxdId={currentUser.letterboxdId}
              userId={currentUser.id}
              activityKey={key}
              onBadgeClick={setSelectedBadgeKey}
            />
          ),
      },
    },
    {
      key: 'letterboxdDisplay',
      label: copy.Letterboxd,
      state: hasConnectedLetterboxd ? 'complete' : 'disabled',
      canHide: false,
      renderState: {
        complete: () =>
          currentUser.letterboxdId && (
            <div className="w-full max-w-sm rounded-xl bg-purple-900 px-3 pt-4 pb-2">
              <LetterboxdBar
                hideBadges
                className="w-full"
                userId={currentUser.id}
                letterboxdId={currentUser.letterboxdId}
                purpleBackgroundMode
                errorElement={
                  <>
                    <div className="mb-4 flex items-center justify-between">
                      <div className="text-sm font-medium">
                        {copy['Letterboxd Activity']}
                      </div>
                      <Action
                        className="!p-0 !text-xs"
                        onClick={() => {
                          setShowLetterboxdModal(true);
                        }}
                      >
                        {copy.Settings}
                      </Action>
                    </div>
                    <div className="flex items-center gap-2 rounded-lg bg-purple-800 py-3 px-4 text-purple-300">
                      <IconAlertTriangle className="h-4 w-4 flex-none" />
                      <div className="text-sm">
                        {copy['Letterboxd is unavailable right now']}
                      </div>
                    </div>
                  </>
                }
              />
            </div>
          ),
      },
    },
  ];

  let visibleActivities = activities.filter(
    (activity) => currentUser.hiddenActivityKeys?.[activity.key] !== true,
  );

  let nextIncompleteActivityKey = visibleActivities.find(
    (activity) => activity.state === 'new' || activity.state === 'started',
  )?.key;

  let body = (
    <div className="mx-3 min-h-0 flex-1 ">
      <div className="relative h-full overflow-y-auto">
        <div className="flex min-h-full flex-col items-center justify-center gap-6 py-8">
          {/* dummy div to account for height of browse leagues bar */}
          <div className="h-[50px]"></div>
          <div className=" flex flex-col items-center gap-4">
            <PopWrapper>
              <AvatarArtworkComponent
                className="h-24 w-24 overflow-hidden rounded-full"
                // @ts-expect-error issue with avatar background props
                backgroundColor={currentUser.avatarBackgroundHex}
                onClick={() => {
                  setShowChooseAvatarModal(true);
                }}
              />
            </PopWrapper>
            <div>
              {!hasStartedBallot && !isBallotComplete ? copy.welcome : copy.hi},{' '}
              {currentUser.handle}.
            </div>
          </div>
          {visibleActivities.map((activity) => {
            const body = activity.renderState[activity.state]?.({
              isNextIncompleteActivity:
                activity.key === nextIncompleteActivityKey,
              key: activity.key,
            });

            if (!body) return null;
            return <Fragment key={activity.key}>{body}</Fragment>;
          })}
          {activities.length - visibleActivities.length > 0 && (
            <div
              className="mt-6 cursor-pointer rounded-xl bg-purple-900 py-2 px-4 text-center text-sm text-purple-400"
              onClick={() => {
                setShowHiddenItems(!showHiddenItems);
              }}
            >
              <div>
                {pluralize({
                  count: activities.length - visibleActivities.length,
                  singular: copy.Item,
                  plural: copy.Items,
                })}{' '}
                {copy.hidden}
              </div>
              {showHiddenItems &&
                Object.entries(currentUser.hiddenActivityKeys || {})
                  .filter(([key, value]) => value === true)
                  .map(([key]) => (
                    <div key={key} className="mt-3">
                      {activities.find((activity) => activity.key === key)
                        ?.label || key}
                      <span
                        className="text-rose-bright ml-3"
                        onClick={() => {
                          setHiddenActivityKeyMutation({
                            userId: currentUser.id,
                            activityKey: key,
                            hidden: false,
                          });
                        }}
                      >
                        Show
                      </span>
                    </div>
                  ))}
            </div>
          )}
          {/* dummy div to account for overlap of sticky menu */}
          <div className="h-[70px]"></div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <LetterboxdModal
        onClose={() => {
          setShowLetterboxdModal(false);
        }}
        isOpen={showLetterboxdModal}
      />
      <ChooseAvatarModal
        isOpen={showChooseAvatarModal}
        onClose={() => {
          setShowChooseAvatarModal(false);
        }}
        onChoose={(avatarId) => {
          updateUserPropertiesMutation({
            userId: currentUser.id,
            avatarId,
          });
        }}
      />
      <ComposeBallotModal
        isOpen={showComposeBallotModal}
        onCloseStart={() => {
          setIsEnvelopeOpen(false);
        }}
        onClose={() => {
          setShowComposeBallotModal(false);
        }}
      />
      <CreateLeagueModal
        isOpen={showCreateLeagueModal}
        onClose={() => {
          setShowCreateLeagueModal(false);
        }}
      />
      <LeagueModal
        leagueId={showLeagueModalId}
        isOpen={Boolean(showLeagueModalId)}
        onClose={() => {
          setShowLeagueModalId(undefined);
        }}
      />
      <AllUsersModal
        isOpen={showAllUsersModal}
        onClose={() => {
          setShowAllUsersModal(false);
        }}
      />
      <NomineesModal
        isOpen={showNomineesModal}
        onClose={() => {
          setShowNomineesModal(false);
        }}
        showFinishBallot={!isBallotComplete}
        onFinishBallot={() => {
          setShowNomineesModal(false);
          setShowComposeBallotModal(true);
        }}
      />
      {currentUser.letterboxdId && (
        <BadgeFilmsModal
          badgeKey={selectedBadgeKey}
          letterboxdId={currentUser.letterboxdId}
          isOpen={selectedBadgeKey !== null}
          onClose={() => setSelectedBadgeKey(null)}
        />
      )}
      {presentationMode ? (
        <div className="flex min-h-0 flex-1 flex-col">
          {Boolean(leagues.length) && (
            <div className="h-[66px] flex-none px-3 pt-2">
              <LeagueBar
                leagues={leagues}
                completedBallots={completedBallots}
                onSelectLeague={(leagueId) => {
                  if (leagueId === undefined) {
                    setShowAllUsersModal(true);
                  } else {
                    setShowLeagueModalId(leagueId);
                  }
                }}
                countMode={LeagueBarCountMode.members}
                disableEveryone={!isBallotReady}
                showCreateButton
                onCreateLeague={() => setShowCreateLeagueModal(true)}
                fullScreen={Boolean(presentationMode)}
                className={makeClassName(
                  'z-[2] mb-[-60px] flex h-[60px] items-center overflow-x-auto overflow-y-hidden px-4 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                  presentationMode && 'mr-24',
                )}
              />
            </div>
          )}
          <div className="flex min-h-0 flex-1">
            <div className="flex min-h-0 min-w-0 max-w-lg flex-1 flex-col px-6 pb-6 pt-2">
              <CeremonyDetailCard className="flex-1" />
            </div>
            {body}
          </div>
        </div>
      ) : (
        <>
          <PersistentBanner ballot={ballot} />
          {Boolean(leagues.length) && (
            <LeagueBar
              leagues={leagues}
              completedBallots={completedBallots}
              onSelectLeague={(leagueId) => {
                if (leagueId === undefined) {
                  setShowAllUsersModal(true);
                } else {
                  setShowLeagueModalId(leagueId);
                }
              }}
              countMode={LeagueBarCountMode.members}
              disableEveryone={!isBallotReady}
              showCreateButton
              onCreateLeague={() => setShowCreateLeagueModal(true)}
              className="z-[2] mb-[-60px] flex h-[60px] items-center overflow-x-auto overflow-y-hidden px-4 py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            />
          )}
          {body}
        </>
      )}
    </>
  );
};

export default CeremonyBefore;
